import { useState } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { useAllTickets } from '@/hooks/useTickets';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { StatusBadge, TypeBadge, PriorityBadge } from '@/components/Badges';
import { timestampToDate, TicketStatus, TicketType, TicketChannel } from '@/types';

type SortField = 'created' | 'status' | 'priority' | 'type';

export function AdminTicketsPage() {
  const [statusFilter, setStatusFilter] = useState<TicketStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<TicketType | ''>('');
  const [channelFilter, setChannelFilter] = useState<TicketChannel | ''>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('created');
  const [sortAsc, setSortAsc] = useState(false);

  const { tickets, loading, loadingMore, error, hasMore, loadMore } = useAllTickets({
    status: statusFilter || undefined,
    type: typeFilter || undefined,
    channel: channelFilter || undefined,
    search: searchQuery || undefined,
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  };

  const sortedTickets = [...tickets].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case 'created':
        comparison = (timestampToDate(a.createdAt)?.getTime() || 0) - (timestampToDate(b.createdAt)?.getTime() || 0);
        break;
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'priority':
        const priorityOrder = { LOW: 0, MEDIUM: 1, HIGH: 2 };
        comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
    }
    return sortAsc ? comparison : -comparison;
  });

  const stats = {
    total: sortedTickets.length,
    open: sortedTickets.filter((t) => t.status === 'OPEN').length,
    inProgress: sortedTickets.filter((t) => t.status === 'IN_PROGRESS').length,
    resolved: sortedTickets.filter((t) => t.status === 'RESOLVED').length,
    closed: sortedTickets.filter((t) => t.status === 'CLOSED').length,
  };

  if (loading) return <ProtectedRoute requireAdmin><Layout><LoadingSpinner /></Layout></ProtectedRoute>;
  if (error) return (
    <ProtectedRoute requireAdmin><Layout>
      <div className="terminal-alert error">Error: {error.message}</div>
    </Layout></ProtectedRoute>
  );

  return (
    <ProtectedRoute requireAdmin>
      <Layout>
        {/* Stats */}
        <div className="admin-stats-grid">
          {[
            { label: 'TOTAL', value: stats.total, color: 'var(--text)' },
            { label: 'OPEN', value: stats.open, color: '#64aaff' },
            { label: 'IN PROGRESS', value: stats.inProgress, color: 'var(--gold)' },
            { label: 'RESOLVED', value: stats.resolved, color: 'var(--success)' },
            { label: 'CLOSED', value: stats.closed, color: 'var(--muted)' },
          ].map(({ label, value, color }) => (
            <div key={label} className="terminal-card" style={{ marginBottom: 0 }}>
              <div className="card-body" style={{ padding: '12px', gap: '4px' }}>
                <div style={{ fontSize: '9px', color: 'var(--muted)', letterSpacing: '0.12em' }}>{label}</div>
                <div style={{ fontSize: '22px', color, fontWeight: 700 }}>{value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="terminal-card">
          <div className="card-header">
            <span className="card-num">01</span>
            <span className="card-title">FILTERS</span>
          </div>
          <div className="card-body">
            <div className="admin-filters-grid">
              <div className="field-group">
                <label className="field-label">STATUS</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as TicketStatus | '')} className="terminal-select">
                  <option value="">ALL STATUSES</option>
                  <option value="OPEN">OPEN</option>
                  <option value="IN_PROGRESS">IN PROGRESS</option>
                  <option value="RESOLVED">RESOLVED</option>
                  <option value="CLOSED">CLOSED</option>
                </select>
              </div>
              <div className="field-group">
                <label className="field-label">TYPE</label>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as TicketType | '')} className="terminal-select">
                  <option value="">ALL TYPES</option>
                  <option value="ISSUE">ISSUE</option>
                  <option value="SERVICE_REQUEST">SERVICE REQUEST</option>
                </select>
              </div>
              <div className="field-group">
                <label className="field-label">CHANNEL</label>
                <select value={channelFilter} onChange={(e) => setChannelFilter(e.target.value as TicketChannel | '')} className="terminal-select">
                  <option value="">ALL CHANNELS</option>
                  <option value="WEB">WEB</option>
                  <option value="TELEGRAM">TELEGRAM</option>
                  <option value="DISCORD">DISCORD</option>
                </select>
              </div>
              <div className="field-group">
                <label className="field-label">SEARCH</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search tickets…"
                  className="terminal-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tickets table */}
        <div className="terminal-card">
          <div className="card-header">
            <span className="card-num">02</span>
            <span className="card-title">ALL TICKETS</span>
            <div className="card-header-right">
              <span className="stat-chip">SHOWING <strong>{sortedTickets.length}</strong></span>
            </div>
          </div>
          <div className="card-body">
            {sortedTickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--muted)', fontSize: '10px' }}>
                No tickets found
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '9px', color: 'var(--muted)', letterSpacing: '0.12em' }}>SORT:</span>
                  {(['created', 'status', 'priority', 'type'] as SortField[]).map(field => (
                    <button key={field} onClick={() => handleSort(field)} className={`sort-btn${sortField === field ? ' active' : ''}`}>
                      {field.toUpperCase()} {sortField === field && (sortAsc ? '^' : 'v')}
                    </button>
                  ))}
                </div>

                {/* Mobile cards — visible below 768px */}
                <div className="ticket-cards-mobile">
                  {sortedTickets.map((ticket) => (
                    <Link key={ticket.id} to={`/admin/tickets/${ticket.id}`} className="ticket-mobile-card">
                      <div className="ticket-mobile-card-top">
                        <span className="ticket-mobile-card-id">#{ticket.id.substring(0, 8).toUpperCase()}</span>
                        <span className="ticket-mobile-card-date">
                          {timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'MMM d, yy') : '—'}
                        </span>
                      </div>
                      <div className="ticket-mobile-card-title">{ticket.title}</div>
                      <div className="ticket-mobile-card-badges">
                        <StatusBadge status={ticket.status} />
                        <PriorityBadge priority={ticket.priority} />
                        <TypeBadge type={ticket.type} />
                        <span className="term-badge">{ticket.channel}</span>
                      </div>
                      <div className="ticket-mobile-card-footer">{ticket.ownerEmail}</div>
                    </Link>
                  ))}
                </div>

                {/* Desktop table — visible 768px+ */}
                <div className="ticket-table-desktop">
                  <div className="table-wrap">
                    <table className="terminal-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>TITLE</th>
                          <th>STATUS</th>
                          <th>PRIORITY</th>
                          <th>TYPE</th>
                          <th>CHANNEL</th>
                          <th>FROM</th>
                          <th>CREATED</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedTickets.map((ticket) => (
                          <tr key={ticket.id}>
                            <td>
                              <Link to={`/admin/tickets/${ticket.id}`} style={{ fontFamily: 'Consolas, monospace', fontSize: '9px' }}>
                                #{ticket.id.substring(0, 8).toUpperCase()}
                              </Link>
                            </td>
                            <td style={{ maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <Link to={`/admin/tickets/${ticket.id}`}>{ticket.title}</Link>
                            </td>
                            <td><StatusBadge status={ticket.status} /></td>
                            <td><PriorityBadge priority={ticket.priority} /></td>
                            <td><TypeBadge type={ticket.type} /></td>
                            <td><span className="term-badge">{ticket.channel}</span></td>
                            <td style={{ color: 'var(--muted)', fontSize: '9px' }}>{ticket.ownerEmail}</td>
                            <td style={{ color: 'var(--muted)', fontSize: '9px' }}>
                              {timestampToDate(ticket.createdAt) ? format(timestampToDate(ticket.createdAt)!, 'MMM d, yy') : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                {hasMore && (
                  <div>
                    <button onClick={loadMore} disabled={loadingMore} className="action-btn secondary" style={{ width: '100%' }}>
                      {loadingMore ? 'Loading…' : 'Load More'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Layout>
    </ProtectedRoute>
  );
}
